import { Button, Ellipsis, HybridTooltip, Icon } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import LinearLoader from '@trustoo/components/ui/LinearLoader'
import { deleteAnonymizedRequest } from 'actions/anonymizedRequests'
import { fetchServices } from 'actions/services'
import AnonymizedRequestEditModal from 'components/marketing/anonymizedRequests/AnonymizedRequestEditModal'
import TableList from 'components/TableList'
import FormattedTime from 'components/ui/FormattedTime'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useList } from 'hooks/listHooks'
import { useDeleteModal } from 'hooks/modalHooks'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import anonymizedRequestsSchema from 'schemes/anonymizedRequestsSchema'

interface AnonymizedRequestDeleteActionProps {
    anonymizedRequestId: number
}

const AnonymizedRequestDeleteAction = ({
    anonymizedRequestId,
}: AnonymizedRequestDeleteActionProps) => {
    const dispatch = useAppDispatch()
    const [isDeleting, setIsDeleting] = useState(false)

    const handleDelete = async () => {
        setIsDeleting(true)
        try {
            await dispatch(deleteAnonymizedRequest(anonymizedRequestId))
        } finally {
            setIsDeleting(false)
        }
    }

    const { openModal: openDeleteModal, modal: deleteModal } = useDeleteModal(
        isDeleting,
        handleDelete,
        'anonymized request',
    )

    return (
        <>
            <Button name="text" onClick={openDeleteModal} disabled={isDeleting}>
                <Icon name="delete_forever" />
            </Button>
            {deleteModal}
        </>
    )
}

const definitions = {
    request_id: { name: 'Original request', preventRedirect: true },
    service: { name: 'Service' },
    location: { name: 'Location' },
    subject: { name: 'Subject' },
    presentation_score: { name: 'Presentation' },
    commercial_score: { name: 'Commercial' },
    created: { name: 'Original request creation' },
    actions: { name: 'Actions', preventRedirect: true },
}

interface ScoreWithReasonProps {
    score: number
    reason: string
}

const ScoreWithReason = ({ score, reason }: ScoreWithReasonProps) => (
    <span className="inline-flex items-center gap-1">
        {score}
        <HybridTooltip
            className="z-10"
            childrenContent={<div className="max-w-xs text-sm leading-relaxed">{reason}</div>}>
            <span>
                <Icon name="info" fontSize={16} className="cursor-help text-neutral-500" />
            </span>
        </HybridTooltip>
    </span>
)

const useAnonymizedRequestsList = (filterId: string) =>
    useList(
        filterId,
        {
            url: 'office/anonymized-requests/get-list',
            schema: anonymizedRequestsSchema,
            reducerKey: 'anonymizedRequests',
        },
        {},
    )

interface AnonymizedRequestsListProps {
    filterId?: string
}

const AnonymizedRequestsList = ({ filterId = 'all' }: AnonymizedRequestsListProps) => {
    const dispatch = useAppDispatch()
    const { entities, ...list } = useAnonymizedRequestsList(filterId)
    const anonymizedRequests = entities.anonymizedRequests
    const requests = entities.requests
    const services = useAppSelector((state) => state.services.entities.services)
    useEffect(() => {
        dispatch(fetchServices())
    }, [dispatch])

    if (Object.keys(services).length === 0) {
        return <LinearLoader absolute />
    }

    const renderRow = (anonymizedRequestId: number) => {
        const anonymizedRequest = anonymizedRequests[anonymizedRequestId]
        const request = requests[anonymizedRequest.request_id]
        const evaluation = request.request_marketing_evaluation
        const serviceName = services[request.service_id].name
        const location = request.postal_code?.place_name ?? ''
        const subject = request.subject || ''
        const subjectExcerpt = subject.substring(subject.indexOf('(') + 1).replace(')', '')

        return {
            cols: [
                <Link to={`/service/requests/request/${anonymizedRequest.request_id}`}>
                    <Button name="link">{anonymizedRequest.request_id}</Button>
                </Link>,
                serviceName,
                location,
                <Ellipsis style={{ maxWidth: 200 }}>{subjectExcerpt}</Ellipsis>,
                <ScoreWithReason
                    score={evaluation.presentation_score}
                    reason={evaluation.presentation_reason}
                />,
                <ScoreWithReason
                    score={evaluation.commercial_score}
                    reason={evaluation.commercial_reason}
                />,
                <small className="c-grey to-right">
                    <FormattedTime date={request.created} />
                </small>,
                <div className="flex items-center">
                    <Button
                        name="text"
                        onClick={() =>
                            NiceModal.show(AnonymizedRequestEditModal, {
                                anonymizedRequestId,
                            })
                        }>
                        <Icon name="edit" />
                    </Button>
                    <AnonymizedRequestDeleteAction anonymizedRequestId={anonymizedRequestId} />
                </div>,
            ],
        }
    }
    return <TableList definitions={definitions} renderRow={renderRow} {...list} />
}

export default AnonymizedRequestsList
